import useAuth from './use-auth';
import {
  getPreviewActivity,
  getPreviewDashboard,
  getProsumerDashboard,
  getProjectExamples,
  getProsumerProfile,
  getProsumerActivity,
  getStreakStats,
  AnswerState,
  getLearningPlanUpsell,
} from '../hydra';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useQuery } from 'react-query';
import { EVENTS, trackEvent } from '../analytics';
import { getTeamsProfile } from '../api/teams';

export const queryOptions = {
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

export function useTeamsProfile() {
  const { getTokenSilently, isAuthenticated } = useAuth();
  const state = useQuery(
    ['get-teams-profile', isAuthenticated],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      const home = await getTeamsProfile(token);

      return home;
    },
    queryOptions
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}

export function useStreakStats() {
  const { getTokenSilently, isAuthenticated } = useAuth();
  const state = useQuery(
    ['get-streak-stats', isAuthenticated],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      const stats = await getStreakStats(token);
      return stats;
    },
    queryOptions
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}

export function usePreviewActivity(missionSlug: string, activitySlug: string) {
  const { getTokenSilently, isAuthenticated } = useAuth();
  const query = useLocation();

  const state = useQuery(
    ['get-preview-activity', isAuthenticated, missionSlug, activitySlug],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      const mission = await getPreviewActivity(token, {
        missionSlug,
        activitySlug,
      });

      let answerStates: Record<string, AnswerState> = {};
      try {
        const parsed = queryString.parse(query.search);

        const stringifiedAnswerStates =
          parsed.stringifiedAnswerStates as string;

        if (stringifiedAnswerStates) {
          answerStates = JSON.parse(stringifiedAnswerStates);
        }
      } catch (err) {
        console.error('cannot parse solutions query');
      }

      return {
        ...mission,
        latest_submission: {
          id: 999,
          updated_at: new Date().toString(),
          created_at: new Date().toString(),
          difficulty_rating: null,
          feedback: null,
          seconds_spent: null,
          activity_session_id: 999,
          ai_review: null,
          answer_states: answerStates,
          mentor_reviews: mission.latest_submission?.mentor_reviews ?? [],
        },
      };
    },
    queryOptions
  );

  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}

export function useLearningPlanUpsell() {
  const { getTokenSilently } = useAuth();
  const state = useQuery(
    ['get-learning-plan-upsell'],
    async () => {
      const token = await getTokenSilently();
      const learningPlan = await getLearningPlanUpsell(token);
      return learningPlan;
    },
    {
      refetchOnWindowFocus: false,
    }
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}

export function useProsumerActivity(missionSlug: string, activitySlug: string) {
  const { getTokenSilently, isAuthenticated } = useAuth();
  const state = useQuery(
    ['get-prosumer-activity', isAuthenticated, missionSlug, activitySlug],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      const mission = await getProsumerActivity(token, {
        missionSlug,
        activitySlug,
      });
      return mission;
    },
    queryOptions
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}

export function usePreviewDashboard(contentSlug: string) {
  const { getTokenSilently, isAuthenticated } = useAuth();
  const location = useLocation();
  const state = useQuery(
    ['get-preview-stats', isAuthenticated, contentSlug, location.pathname],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      const stats = await getPreviewDashboard(token, { contentSlug });
      return stats;
    },
    queryOptions
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}

export function useProsumerDashboard(contentSlug: string) {
  const { getTokenSilently, isAuthenticated } = useAuth();
  const location = useLocation();
  const state = useQuery(
    ['get-prosumer-stats', isAuthenticated, contentSlug, location.pathname],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      const stats = await getProsumerDashboard(token, { contentSlug });
      return stats;
    },
    queryOptions
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}

export function useProjectExamples() {
  const { getTokenSilently, isAuthenticated } = useAuth();
  const state = useQuery(
    ['get-project-examples', isAuthenticated],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      const examples = await getProjectExamples(token);
      return examples;
    },
    queryOptions
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}

export function useGetProsumerProfile() {
  const { getTokenSilently, isAuthenticated } = useAuth();

  const state = useQuery(
    ['prosumer-profile', isAuthenticated],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      try {
        const profile = await getProsumerProfile(token);

        // TODO: why do we need this, doesn't react query capture the error properly?
        if (!profile?.hasProfile) {
          return null;
        }

        trackEvent({
          event: EVENTS.PROSUMER.PING,
        });
        return profile;
      } catch (error) {
        console.error('error fetching prosumer profile', error);
        return null;
      }
    },
    queryOptions
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}
